import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Box,
} from '@mui/material';
import { Contact } from '../../types/contact.types';
import { getClientContacts } from '../../api/contacts';

interface ContactSelectDialogProps {
  open: boolean;
  clientId?: string;
  title: string;
  description?: string;
  confirmLabel?: string;
  onCancel: () => void;
  // undefined contactId = use the role-based default (active PRIMARY contact)
  onConfirm: (contactId?: string) => void;
}

// Audit 07 R-5/F-105: deliberate send-target chooser. The user picks the
// Contact the artefact is addressed to, or explicitly keeps the role-based
// default (active PRIMARY). Never an incidental field chain.
const ContactSelectDialog: React.FC<ContactSelectDialogProps> = ({
  open,
  clientId,
  title,
  description,
  confirmLabel = 'Confirm',
  onCancel,
  onConfirm,
}) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !clientId) return;
    setSelected('');
    setLoading(true);
    getClientContacts(clientId)
      .then(setContacts)
      .catch(() => setContacts([]))
      .finally(() => setLoading(false));
  }, [open, clientId]);

  const primary = contacts.find(c => c.roles.includes('PRIMARY'));

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {description}
          </Typography>
        )}
        {loading ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="contact-select-label">Send to contact</InputLabel>
            <Select
              labelId="contact-select-label"
              value={selected}
              label="Send to contact"
              onChange={(e) => setSelected(e.target.value)}
            >
              <MenuItem value="">
                <em>
                  {primary
                    ? `Default — ${primary.name}${primary.email ? ` (${primary.email})` : ''} [PRIMARY]`
                    : 'Default — primary contact'}
                </em>
              </MenuItem>
              {contacts.map((c) => (
                <MenuItem key={c._id} value={c._id}>
                  {c.name}
                  {c.email ? ` — ${c.email}` : ''}
                  {c.roles?.length ? ` [${c.roles.join(', ')}]` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {!loading && contacts.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            No contacts recorded for this client yet — the client's legacy contact details will be used.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => onConfirm(selected || undefined)}
          disabled={loading}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactSelectDialog;
